/**
 * Structured Phase 1 text block for prompts (Tier 1 KB, can’t-miss, gaps).
 * @param {import('./ddxTypes.js').DdxKnowledgeBundle} bundle
 * @param {{ includeTemporalHistory?: boolean, includeExpectedButMissing?: boolean, includeCalculatorResults?: boolean }} [options]
 * @returns {string}
 */
function formatShiftRationaleSection(shiftScores, topDiagnosisIds) {
  if (!shiftScores || typeof shiftScores !== 'object') return '';
  const keys = Object.keys(shiftScores);
  if (!keys.length) return '';

  const lines = [
    '--- SHIFT SIGNATURE ANALYSIS ---',
    'Clinical findings that significantly shift the differential:',
    '',
  ];

  for (const diagnosisId of (topDiagnosisIds || []).slice(0, 5)) {
    const score = shiftScores[diagnosisId];
    if (!score || !Array.isArray(score.edges) || score.edges.length === 0) continue;
    if (score.suppressedBy) {
      // Only reviewed negating/refuting edges set suppressedBy — pending edges never appear here.
      lines.push(`${diagnosisId}: SUPPRESSED (${score.suppressedBy})`);
      continue;
    }

    lines.push(`${diagnosisId} (shift score: ${(Number(score.shiftScore) || 0).toFixed(1)}):`);
    for (const edge of score.edges.slice(0, 3)) {
      const calcNote =
        edge?.calculatorDerived?.calculatorName
          ? ` [${edge.calculatorDerived.calculatorName}]`
          : '';
      lines.push(
        `  • ${edge.factorId}: ${edge.shiftDirection}${calcNote} — ${edge.clinicalRationale}`
      );
    }
    lines.push('');
  }

  lines.push(
    'Trust note: Reviewed and pending-review edges are weighted equally for scoring; review status is shown for clinician confidence only.',
    '--- END SHIFT SIGNATURE ANALYSIS ---'
  );
  return lines.join('\n');
}

export function formatPhase1BundleForPrompt(bundle, options = {}) {
  const includeTemporalHistory = options.includeTemporalHistory !== false;
  const includeExpectedButMissing = options.includeExpectedButMissing !== false;
  const includeCalculatorResults = options.includeCalculatorResults !== false;
  if (
    !bundle?.candidates?.length &&
    !bundle?.cantMiss?.length &&
    !(bundle?.tier2ProvisionalFromSources || []).length
  ) {
    return '';
  }
  const lines = [
    '## DDX-Plus Phase 1 — KB-grounded context (Tier 1 verified)',
    `Temporal bucket: ${bundle.temporalBucket || 'unknown'}`,
    `Bundle hash: ${bundle.bundleHash || 'n/a'}`,
    '',
    '### Scored candidates (sorted)',
    JSON.stringify(bundle.candidates || [], null, 0),
    '',
    "### Can’t-miss (must be surfaced explicitly for clinician action — not only in DDx list)",
    JSON.stringify(bundle.cantMiss || [], null, 0),
  ];
  if (includeExpectedButMissing) {
    lines.push(
      '',
      '### Expected-but-missing (high-sensitivity features not extracted — consider asking / examining)',
      JSON.stringify(bundle.expectedButMissing || [], null, 0)
    );
  }
  if ((bundle.nextBestActions || []).length) {
    lines.push(
      '',
      '### Next-best actions (Phase 2 — discriminating asks / exam / orders)',
      JSON.stringify(bundle.nextBestActions, null, 0)
    );
  }
  if (includeCalculatorResults && (bundle.calculatorSuggestions || []).length) {
    lines.push('', '### Calculator suggestions', JSON.stringify(bundle.calculatorSuggestions, null, 0));
  }
  if (includeTemporalHistory && bundle.ccResolution && typeof bundle.ccResolution === 'object') {
    lines.push('', '### Chief complaint resolution (Phase 3)', JSON.stringify(bundle.ccResolution, null, 0));
  }
  if ((bundle.tier2ProvisionalFromSources || []).length) {
    lines.push(
      '',
      '### Tier 2 provisional (authoritative retrieval — unverified; flag to clinician; KB verified content above takes precedence)',
      JSON.stringify(bundle.tier2ProvisionalFromSources, null, 0)
    );
  }
  if ((bundle.sourceGroundingProvenance || []).length) {
    lines.push('', '### Provenance', (bundle.sourceGroundingProvenance || []).join('\n'));
  }
  if (bundle.shiftScores && typeof bundle.shiftScores === 'object') {
    const topDiagnosisIds = (bundle.candidates || []).map((c) => c.diagnosisId).filter(Boolean);
    const shiftSection = formatShiftRationaleSection(bundle.shiftScores, topDiagnosisIds);
    if (shiftSection) {
      lines.push('', shiftSection);
    }
  }
  return lines.join('\n');
}

/**
 * Build a bounded text block from a DdxKnowledgeBundle for LLM system grounding.
 *
 * @param {Object} bundle - DdxKnowledgeBundle-shaped object
 * @param {{ maxApproxChars?: number }} [options]
 * @returns {{ text: string, truncated: boolean, approxChars: number }}
 */
export function projectDdxKnowledgeForSession(bundle, options = {}) {
  const maxApproxChars = options.maxApproxChars ?? 14_000;
  const bundleComposition = options.bundleComposition && typeof options.bundleComposition === 'object'
    ? options.bundleComposition
    : {};
  const includeTemporalHistory = bundleComposition.includeTemporalHistory !== false;
  const includeExpectedButMissing = bundleComposition.includeExpectedButMissing !== false;
  const includeCalculatorResults = bundleComposition.includeCalculatorResults !== false;
  if (!bundle || typeof bundle !== 'object') {
    return { text: '', truncated: false, approxChars: 0 };
  }

  const parts = [];

  if (bundle.chiefComplaintId) {
    parts.push(`[Chief complaint id: ${bundle.chiefComplaintId}]`);
  }
  if (bundle.kbVersion) {
    parts.push(`[KB version: ${bundle.kbVersion}]`);
  }
  const p1 =
    bundle.phase1GroundingText && String(bundle.phase1GroundingText).trim()
      ? String(bundle.phase1GroundingText).trim()
      : formatPhase1BundleForPrompt(bundle, {
          includeTemporalHistory,
          includeExpectedButMissing,
          includeCalculatorResults,
        });
  if (p1) {
    parts.push(p1);
  }
  if (bundle.cantMissSnippet && String(bundle.cantMissSnippet).trim()) {
    parts.push('## Can’t-miss considerations\n' + String(bundle.cantMissSnippet).trim());
  }
  if (includeTemporalHistory && bundle.chiefComplaint && typeof bundle.chiefComplaint === 'object') {
    parts.push('## Chief complaint (structured)\n' + JSON.stringify(bundle.chiefComplaint));
  }
  if (Array.isArray(bundle.diagnoses) && bundle.diagnoses.length > 0) {
    parts.push('## Diagnoses (subset)\n' + JSON.stringify(bundle.diagnoses));
  }
  if (includeTemporalHistory && Array.isArray(bundle.features) && bundle.features.length > 0) {
    parts.push('## Features (subset)\n' + JSON.stringify(bundle.features));
  }
  if (includeTemporalHistory && Array.isArray(bundle.edges) && bundle.edges.length > 0) {
    parts.push('## Feature–diagnosis edges (subset)\n' + JSON.stringify(bundle.edges));
  }
  if (includeExpectedButMissing && Array.isArray(bundle.nextBestActions) && bundle.nextBestActions.length > 0) {
    parts.push('## Next-best actions\n' + JSON.stringify(bundle.nextBestActions));
  }
  if (includeExpectedButMissing && Array.isArray(bundle.expectedButMissing) && bundle.expectedButMissing.length > 0) {
    parts.push('## Expected-but-missing\n' + JSON.stringify(bundle.expectedButMissing));
  }
  if (includeCalculatorResults && Array.isArray(bundle.calculatorSuggestions) && bundle.calculatorSuggestions.length > 0) {
    parts.push('## Calculator suggestions\n' + JSON.stringify(bundle.calculatorSuggestions));
  }

  let text = parts.join('\n\n');
  let truncated = false;
  if (text.length > maxApproxChars) {
    text = text.slice(0, maxApproxChars) + '\n\n[…truncated for token budget]';
    truncated = true;
  }

  return { text, truncated, approxChars: text.length };
}
